import { useEffect, useRef, useState, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { t } from '../i18n/lingua'

export interface RailItem {
  to: string
  label: string
  end?: boolean
}

export interface RailGroup {
  id: string
  section: string
  items: RailItem[]
}

/**
 * The menu as a column of icons (wide screens, «Icone» mode): one icon per
 * section, and the section's pages in a panel beside it — on hover, on click,
 * or with the keyboard — so a page is two gestures away while the page keeps
 * almost the whole width. The section of the current page is marked; the
 * favourites, when there are some, come first behind a star.
 */
export function NavRail({ groups, currentGroupId, icon, isDisabled, isFavorite, toggleFavorite, onExpand }: {
  groups: RailGroup[]
  currentGroupId: string | undefined
  icon: (id: string) => ReactNode
  isDisabled: (to: string) => boolean
  isFavorite: (to: string) => boolean
  toggleFavorite: (to: string) => void
  onExpand: () => void
}) {
  const [open, setOpen] = useState<{ id: string; top: number } | null>(null)
  const timer = useRef<number | undefined>(undefined)
  const railRef = useRef<HTMLElement | null>(null)
  const flyRef = useRef<HTMLDivElement | null>(null)
  const { pathname } = useLocation()

  useEffect(() => setOpen(null), [pathname])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (railRef.current?.contains(target) || flyRef.current?.contains(target)) return
      setOpen(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null)
    }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  function show(id: string, el: HTMLElement, delay: number) {
    window.clearTimeout(timer.current)
    const top = Math.max(8, Math.min(el.getBoundingClientRect().top, window.innerHeight - 240))
    timer.current = window.setTimeout(() => setOpen({ id, top }), delay)
  }

  function leave() {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setOpen(null), 220)
  }

  const group = open ? groups.find((g) => g.id === open.id) : undefined

  return (
    <>
      <nav ref={railRef} className="navrail" aria-label={t('Navigazione principale')} onMouseLeave={leave}>
        <button type="button" className="navrail__btn navrail__expand" onClick={onExpand}
          title={t('Espandi il menu (tasto [)')} aria-label={t('Espandi il menu')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 7 5 5-5 5M13 7l5 5-5 5" />
          </svg>
        </button>
        <div className="navrail__list">
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              className={`navrail__btn${g.id === currentGroupId ? ' navrail__btn--current' : ''}${open?.id === g.id ? ' navrail__btn--open' : ''}`}
              aria-label={g.section}
              aria-expanded={open?.id === g.id}
              aria-haspopup="true"
              title={g.section}
              onMouseEnter={(e) => show(g.id, e.currentTarget, open ? 0 : 90)}
              onFocus={(e) => show(g.id, e.currentTarget, 0)}
              onClick={(e) => (open?.id === g.id ? setOpen(null) : show(g.id, e.currentTarget, 0))}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight' || e.key === 'Enter') {
                  e.preventDefault()
                  show(g.id, e.currentTarget, 0)
                  window.setTimeout(() => flyRef.current?.querySelector<HTMLElement>('a')?.focus(), 30)
                }
              }}
            >
              {icon(g.id)}
            </button>
          ))}
        </div>
      </nav>
      {open && group && (
        <div
          ref={flyRef}
          className="navrail__fly"
          style={{ top: open.top }}
          role="menu"
          aria-label={group.section}
          onMouseEnter={() => window.clearTimeout(timer.current)}
          onMouseLeave={leave}
          onKeyDown={(e) => {
            const links = [...(flyRef.current?.querySelectorAll<HTMLElement>('a') ?? [])]
            const i = links.indexOf(document.activeElement as HTMLElement)
            if (e.key === 'ArrowDown') { e.preventDefault(); links[Math.min(links.length - 1, i + 1)]?.focus() }
            if (e.key === 'ArrowUp') { e.preventDefault(); links[Math.max(0, i - 1)]?.focus() }
            if (e.key === 'ArrowLeft') setOpen(null)
          }}
        >
          <div className="navrail__fly-title">{group.section}</div>
          {group.items.map((item) =>
            isDisabled(item.to) ? (
              <span key={item.to} className="navlink navlink--disattivata" aria-disabled="true"
                title={t('Disattivato per il tuo ruolo')}>{item.label}</span>
            ) : (
              <div key={item.to} className="navitem">
                <NavLink to={item.to} end={item.end} role="menuitem"
                  className={({ isActive }) => `navlink${isActive ? ' navlink--active' : ''}`}>
                  {item.label}
                </NavLink>
                <StarButton on={isFavorite(item.to)} onToggle={() => toggleFavorite(item.to)} />
              </div>
            ),
          )}
        </div>
      )}
    </>
  )
}

/** The star of a menu item: adds it to (or removes it from) the favourites. */
export function StarButton({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className={`navstar${on ? ' navstar--on' : ''}`}
      aria-pressed={on}
      aria-label={on ? t('Togli dai preferiti') : t('Aggiungi ai preferiti')}
      title={on ? t('Togli dai preferiti') : t('Aggiungi ai preferiti')}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onToggle()
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
        <path d="m12 3.6 2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.2-4.1 5.8-.8L12 3.6Z"
          fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    </button>
  )
}
